import React, { useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

/**
 * Opportunity type definition for leads
 */
export interface Opportunity {
  readonly id: number;
  readonly name: string;
  readonly account: {
    readonly id: number;
    readonly name: string;
    readonly picture: string;
    readonly hotness?: number;
  };
  readonly stage: {
    readonly id: number;
    readonly name: string;
    readonly color: string;
    readonly order: number;
    readonly probability: number;
  };
  readonly close_date: string;
  readonly assignee: {
    readonly id: number;
    readonly picture: string;
    readonly name: string;
    readonly email: string;
  };
  readonly start_date?: string;
  readonly pipeline?: {
    readonly id: number;
    readonly name: string;
  };
  readonly last_interaction?: {
    readonly type: string;
    readonly description: string;
    readonly date: string;
    readonly person?: {
      readonly id: number;
      readonly picture: string;
      readonly name: string;
    };
  };
  readonly tags?: ReadonlyArray<{
    readonly id: number;
    readonly name: string;
  }>;
  readonly custom?: {
    readonly short_description?: string;
    readonly onboarding_price?: number;
    readonly "node_hardware_($)"?: number;
    readonly node_qty?: number;
    readonly annual_subscription?: number;
    readonly "node_data_service_($)"?: number;
    readonly mws?: number;
    readonly users?: number;
    readonly category?: {
      readonly id: number;
      readonly name: string;
    };
    readonly subscription_type?: {
      readonly id: number;
      readonly name: string;
    };
  };
}

type Columns = Record<string, Opportunity[]>;

const getProbabilityColor = (probability: number) => {
  if (probability >= 0.7) {
    return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200 dark:border-green-800";
  } else if (probability >= 0.4) {
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800";
  }
  return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-800";
};

/**
 * Calculate total revenue from annual subscription and one-time costs
 */
const calculateTotalRevenue = (opportunity: Opportunity): number => {
  const annual = opportunity.custom?.annual_subscription ?? 0;
  const onetime = opportunity.custom?.onboarding_price ?? 0;
  return annual + onetime;
};

/**
 * Presentational card for a single Opportunity
 */
const OpportunityCard: React.FC<{
  readonly opportunity: Opportunity;
  readonly dragOverlay?: boolean;
  readonly onClick?: () => void;
  readonly dragHandleProps?: any; // You can refine this type with @dnd-kit types if needed
}> = React.memo(function OpportunityCard({
  opportunity,
  dragOverlay = false,
  onClick,
  dragHandleProps,
}) {
  return (
    <Card
      onClick={(e) => {
        // @ts-ignore
        if (!e.target.closest(".drag-handle")) {
          console.log("clicked");
          onClick?.();
        }
      }}
      className={cn(
        "bg-card relative min-h-[140px] w-full cursor-pointer rounded-lg border",
        "transition-all duration-200 ease-in-out",
        "hover:border-primary/20 hover:shadow-md",
        "dark:border-border dark:bg-card/90",
        "dark:hover:border-border/60 dark:hover:bg-card",
        dragOverlay && "rotate-[-0.5deg] scale-[1.02] shadow-2xl",
      )}
    >
      {opportunity.account.hotness ? (
        <div className="absolute -right-3.5 -top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-orange-500/80 to-red-500/80 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
          {opportunity.account.hotness}°
        </div>
      ) : null}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pb-2 pt-3">
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <div {...dragHandleProps} className="drag-handle -ml-2 cursor-grab">
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3 className="text-foreground line-clamp-1 cursor-default text-sm font-semibold tracking-tight">
                  {opportunity.name}
                </h3>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-foreground font-medium">
                  {opportunity.name}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Badge
          variant="outline"
          className={cn(
            "rounded-full px-2.5 py-0.5 text-xs font-medium",
            getProbabilityColor(opportunity.stage.probability),
          )}
        >
          {Math.round(opportunity.stage.probability)}%
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-3 pt-0">
        <div className="flex items-center gap-2">
          {opportunity.account.picture && (
            <div className="bg-background/80 dark:bg-background/90 h-5 w-5 flex-shrink-0 rounded-sm p-0.5 shadow-sm">
              <img
                src={opportunity.account.picture}
                alt={opportunity.account.name}
                className="h-full w-full object-contain"
              />
            </div>
          )}
          <TooltipProvider delayDuration={1000}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground line-clamp-1 cursor-default font-medium">
                  {opportunity.account.name}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{opportunity.account.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Badge
                variant="secondary"
                className="bg-primary/5 text-primary hover:bg-primary/10"
              >
                ${calculateTotalRevenue(opportunity).toLocaleString()}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground/70 text-xs">
                {new Date(opportunity.close_date).toLocaleDateString(
                  undefined,
                  {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  },
                )}
              </span>
            </div>
          </div>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Avatar className="ring-primary/10 h-7 w-7 cursor-default transition-all hover:ring-2">
                    <AvatarImage
                      src={opportunity.assignee.picture}
                      alt={opportunity.assignee.name}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-primary/5 text-primary/90 text-xs">
                      {opportunity.assignee.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{opportunity.assignee.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
});

/**
 * Sortable list item for an Opportunity (drag-and-drop handle).
 */
const SortableItem: React.FC<{
  readonly opportunity: Opportunity;
  readonly onClick?: () => void;
}> = React.memo(function SortableItem({ opportunity, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: opportunity.id.toString(),
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    willChange: "transform" as const,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="px-0.5 py-1">
      <OpportunityCard
        opportunity={opportunity}
        onClick={onClick}
        dragHandleProps={listeners}
      />
    </div>
  );
});

interface SalesFlareBoardProps {
  readonly opportunities: readonly Opportunity[];
}

/**
 * A drag-and-drop kanban-like board for leads. Purely local reordering. No side effects on server data.
 */
const SalesFlareBoard: React.FC<SalesFlareBoardProps> = ({ opportunities }) => {
  /**
   * Build columns as: Record<stageName, Opportunity[]>
   */
  const initialColumns = useMemo(() => {
    return opportunities.reduce<Columns>((cols, opp) => {
      const stageName = opp.stage.name;
      return {
        ...cols,
        [stageName]: [...(cols[stageName] || []), opp],
      };
    }, {});
  }, [opportunities]);

  /**
   * Also build a stable stageOrderMap: stageName -> stage.order
   * so we can always sort columns in the same order, even if items move.
   */
  const stageOrderMap = useMemo(() => {
    const map: Record<string, number> = {};
    opportunities.forEach((opp) => {
      // If multiple items share the same stageName, they'll have the same stage.order
      // We'll just set it once. If it changes, last item sets it, but typically it's stable.
      map[opp.stage.name] = opp.stage.order;
    });
    return map;
  }, [opportunities]);

  const [columns, setColumns] = useState<Columns>(initialColumns);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);

  // Sort columns by the stable stageOrderMap, ignoring the actual items in them
  const sortedColumnKeys = useMemo(() => {
    return Object.keys(columns).sort((a, b) => {
      // fallback to 0 if we can't find it
      const orderA = stageOrderMap[a] ?? 0;
      const orderB = stageOrderMap[b] ?? 0;
      return orderA - orderB;
    });
  }, [columns, stageOrderMap]);

  // Utility to check if `id` is actually a column ID
  const isContainer = (id: string) => {
    return sortedColumnKeys.includes(id);
  };

  // Find which column an item (or container) belongs to
  const getContainerFor = (id: string): string | null => {
    if (isContainer(id)) {
      return id;
    }
    // Otherwise, find which column has this item
    return (
      Object.keys(columns).find((columnKey) =>
        columns[columnKey].some((opp) => opp.id.toString() === id),
      ) ?? null
    );
  };

  // For the overlay card
  const findOpportunity = (id: string): Opportunity | null => {
    const colKey = getContainerFor(id);
    if (!colKey) return null;
    return columns[colKey].find((opp) => opp.id.toString() === id) ?? null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    // Clear activeId right away
    setActiveId(null);

    // If there's no drop target, nothing else to do
    if (!over) return;

    const activeIdStr = active.id.toString();
    const overIdStr = over.id.toString();

    const activeContainer = getContainerFor(activeIdStr);
    const overContainer = getContainerFor(overIdStr);

    if (!activeContainer || !overContainer) {
      return;
    }

    // Same container => reordering
    if (activeContainer === overContainer) {
      setColumns((prev) => {
        const newColumns = { ...prev };
        const columnCopy = [...newColumns[activeContainer]]; // copy array

        const oldIndex = columnCopy.findIndex(
          (item) => item.id.toString() === activeIdStr,
        );
        const newIndex = columnCopy.findIndex(
          (item) => item.id.toString() === overIdStr,
        );

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          // Move item in array
          columnCopy.splice(newIndex, 0, columnCopy.splice(oldIndex, 1)[0]);
          newColumns[activeContainer] = columnCopy;
        }
        return newColumns;
      });
    } else {
      // Moving to a different container
      setColumns((prev) => {
        const newColumns = { ...prev };

        const sourceItems = [...newColumns[activeContainer]];
        const activeIndex = sourceItems.findIndex(
          (item) => item.id.toString() === activeIdStr,
        );
        if (activeIndex === -1) {
          return newColumns;
        }

        // Remove from source
        const [movedItem] = sourceItems.splice(activeIndex, 1);

        // Insert into destination
        const destinationItems = [...newColumns[overContainer]];
        const overIndex = destinationItems.findIndex(
          (item) => item.id.toString() === overIdStr,
        );

        if (overIndex === -1) {
          // If dropping into empty space
          destinationItems.push(movedItem);
        } else {
          // Insert at specific index
          destinationItems.splice(overIndex, 0, movedItem);
        }

        newColumns[activeContainer] = sourceItems;
        newColumns[overContainer] = destinationItems;

        return newColumns;
      });
    }
  };

  const activeOpportunity = activeId ? findOpportunity(activeId) : null;

  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex space-x-6 overflow-x-auto">
          {sortedColumnKeys.map((columnId) => {
            const items = columns[columnId] || [];
            return (
              <div
                key={columnId}
                className={cn(
                  "w-[320px] flex-shrink-0 rounded-xl border",
                  "bg-card/40 to-card/80 bg-gradient-to-b backdrop-blur-sm",
                  "dark:bg-card/40 dark:to-card/80",
                  "border-border/50 shadow-sm",
                )}
              >
                <div className="border-border/50 border-b px-4 py-3 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <Badge
                      className={cn(
                        "px-3 py-1 text-sm font-medium",
                        "bg-primary/10 text-primary hover:bg-primary/20",
                        "dark:bg-primary/20 dark:text-primary-foreground dark:hover:bg-primary/30",
                      )}
                    >
                      {columnId}
                    </Badge>
                    <span className="text-muted-foreground text-sm font-medium">
                      {items.length}
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  {/* 
                    Attach the columnId as `id` so dropping an item into empty space 
                    identifies this container by `over.id = columnId`.
                  */}
                  <SortableContext
                    id={columnId}
                    items={items.map((it) => it.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {items.map((opportunity) => (
                        <SortableItem
                          key={opportunity.id.toString()}
                          opportunity={opportunity}
                          onClick={() => setSelectedOpportunity(opportunity)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              </div>
            );
          })}
        </div>
        <DragOverlay>
          {activeOpportunity && (
            <div className="w-[320px]">
              <OpportunityCard opportunity={activeOpportunity} dragOverlay />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <Dialog
        open={selectedOpportunity !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedOpportunity(null);
        }}
      >
        {selectedOpportunity && (
          <DialogContent className="border-border/50 bg-background/95 dark:bg-background/95 mx-auto max-w-2xl rounded-xl p-0 shadow-2xl backdrop-blur-sm">
            <div className="border-border/10 sticky top-0 z-10 border-b bg-inherit p-6">
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <DialogTitle className="text-2xl font-semibold tracking-tight">
                      {selectedOpportunity.name}
                    </DialogTitle>
                    <div className="flex items-center gap-2">
                      <p className="text-muted-foreground/90 font-medium">
                        {selectedOpportunity.account.name}
                      </p>
                      {selectedOpportunity.account.hotness ? (
                        <Badge
                          variant="secondary"
                          className="bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-700 dark:from-orange-500/30 dark:to-red-500/30 dark:text-orange-400"
                        >
                          {selectedOpportunity.account.hotness}° Hot Lead
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "px-3 py-1 text-sm font-medium",
                      getProbabilityColor(
                        selectedOpportunity.stage.probability,
                      ),
                    )}
                  >
                    {Math.round(selectedOpportunity.stage.probability)}%
                    Probability
                  </Badge>
                </div>
              </DialogHeader>
            </div>
            <div className="max-h-[calc(85vh-8rem)] overflow-y-auto">
              <div className="grid gap-6 p-6">
                {/* Key Financial Metrics */}
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Revenue Metrics
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-green-500/5 opacity-50" />
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Annual Recurring Revenue
                        </h4>
                        <p className="text-foreground text-2xl font-semibold tracking-tight">
                          {selectedOpportunity.custom?.annual_subscription
                            ? `$${selectedOpportunity.custom.annual_subscription.toLocaleString()}`
                            : "Not specified"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          One-time Revenue
                        </h4>
                        <p className="text-foreground text-2xl font-semibold tracking-tight">
                          {selectedOpportunity.custom?.onboarding_price
                            ? `$${selectedOpportunity.custom.onboarding_price.toLocaleString()}`
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Metrics */}
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Business Metrics
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-yellow-500/5 opacity-50" />
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Users
                        </h4>
                        <p className="text-foreground text-xl font-semibold tracking-tight">
                          {selectedOpportunity.custom?.users?.toLocaleString() ??
                            0}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          Potential Users
                        </p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Megawatts
                        </h4>
                        <p className="text-foreground text-xl font-semibold tracking-tight">
                          {selectedOpportunity.custom?.mws?.toLocaleString() ??
                            0}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          MW Managed
                        </p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Category
                        </h4>
                        <p className="text-foreground text-xl font-semibold tracking-tight">
                          {selectedOpportunity.custom?.category?.name ??
                            "Not specified"}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          Business Type
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cost Structure */}
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Cost Structure
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-blue-500/5 opacity-50" />
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Hardware Costs
                        </h4>
                        <p className="text-foreground text-lg font-semibold tracking-tight">
                          {selectedOpportunity.custom?.["node_hardware_($)"]
                            ? `$${selectedOpportunity.custom[
                                "node_hardware_($)"
                              ].toLocaleString()}`
                            : "$0"}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          {selectedOpportunity.custom?.node_qty ?? 0} nodes
                        </p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Service Costs
                        </h4>
                        <p className="text-foreground text-lg font-semibold tracking-tight">
                          {selectedOpportunity.custom?.["node_data_service_($)"]
                            ? `$${selectedOpportunity.custom[
                                "node_data_service_($)"
                              ].toLocaleString()}`
                            : "$0"}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          Data & Maintenance
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Deal Timeline */}
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Deal Timeline
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-purple-500/5 opacity-50" />
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Expected Close
                        </h4>
                        <p className="text-foreground font-medium">
                          {selectedOpportunity.close_date
                            ? new Date(
                                selectedOpportunity.close_date,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Not specified"}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{
                              backgroundColor: `rgb(${selectedOpportunity.stage.color})`,
                            }}
                          />
                          <p className="text-muted-foreground text-sm">
                            {selectedOpportunity.stage.name}
                          </p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Service Start
                        </h4>
                        <p className="text-foreground font-medium">
                          {selectedOpportunity.start_date
                            ? new Date(
                                selectedOpportunity.start_date,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Not specified"}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                          {selectedOpportunity.custom?.subscription_type
                            ?.name ?? "Standard"}{" "}
                          Plan
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Account Overview
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="from-primary/5 to-primary/0 absolute inset-0 bg-gradient-to-br opacity-50" />
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-foreground font-medium">
                          {selectedOpportunity.account.name}
                        </p>
                        <div className="text-muted-foreground text-sm">
                          {selectedOpportunity.custom?.short_description ??
                            "No description available"}
                        </div>
                      </div>
                      {selectedOpportunity.account.picture && (
                        <div className="bg-background dark:bg-background h-12 w-12 rounded-lg p-2 shadow-sm">
                          <img
                            src={selectedOpportunity.account.picture}
                            alt={selectedOpportunity.account.name}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default SalesFlareBoard;
